import type { PlayerState, Point, ShotResult } from '../shared/types';
import { terrainBodyAngle, type GroundProbe } from '../shared/shot-geometry';
import deathAssets from '../shared/character-death-assets.json';
import { damageEventAt } from './combat-presentation';

const tickMs = 1000 / deathAssets.sourceRules.frameRate;
export interface DeathGround extends GroundProbe { width: number; height: number }
export interface DeathPhysics { gravity: number; drag: number; wind: number }
export interface DeathFrame {
  startedAt: number; elapsed: number; soulFrame: number;
  soul: Point & { face: -1 | 1 }; tomb: Point & { visible: boolean; moving: boolean; angle: number };
}
export interface VisualDeathFrame extends DeathFrame { nextSoulFrame: number; soulMix: number }
interface Track {
  startedAt: number; pose: Point & { face: -1 | 1 }; physics: DeathPhysics;
  tick: number; tombTick: number; eyeTicks: number; soulFrame: number; random: number;
  tomb: DeathFrame['tomb']; integrated: Point; vx: number; vy: number;
}

/** Death is an event, not the end of the explosion or a fade of the live pose. */
export function deathEventAt(playerId: string, shot: ShotResult): number | null {
  const times = [
    ...shot.damage.filter((event) => event.playerId === playerId && event.killed).map((event) => damageEventAt(event, shot)),
    ...shot.playerTransitions?.filter((event) => event.playerId === playerId && !event.alive)
      .map((event) => event.fall?.hpBeforeDeath !== undefined ? event.fall.startedAt + event.fall.duration : event.at) ?? [],
  ];
  return times.length ? Math.min(...times) : null;
}

/** GamePlayer's two mirrors preserve the body-facing texture, but not its x offset. */
export function soulPose(pose: Point & { face: -1 | 1 }): Point & { face: -1 | 1 } {
  const rules = deathAssets.sourceRules;
  const bodyX = pose.face > 0 ? rules.soulBodyXByFacing.right : rules.soulBodyXByFacing.left;
  return { x: pose.x - pose.face * bodyX,
    y: pose.y + rules.soulRootOffset.y + rules.soulPlayerOffset.y, face: pose.face };
}

export function deathLoopFrame(elapsed: number, count: number, frameRate = 25): number {
  return Math.floor(Math.max(0, elapsed) * frameRate / 1000) % count;
}

/** Separate client-only TombView physics; never changes the server player or terrain. */
export class DeathPresentation {
  private tracks = new Map<string, Track>();

  pose(playerId: string): (Point & { face: -1 | 1 }) | null {
    const pose = this.tracks.get(playerId)?.pose;
    return pose ? { ...pose } : null;
  }

  ensure(player: Pick<PlayerState, 'id' | 'x' | 'y' | 'face'>, startedAt: number, physics: DeathPhysics): void {
    if (this.tracks.has(player.id)) return;
    let seed = 2166136261;
    for (const char of player.id) seed = Math.imul(seed ^ char.charCodeAt(0), 16777619);
    const pose = { x: player.x, y: player.y, face: player.face };
    this.tracks.set(player.id, { startedAt, pose, physics, tick: 0, tombTick: 0, eyeTicks: 1, soulFrame: 1, random: seed >>> 0 || 1,
      tomb: { x: pose.x, y: pose.y, visible: true, moving: true, angle: 0 }, integrated: { x: pose.x, y: pose.y }, vx: 0, vy: 0 });
  }

  retain(players: Pick<PlayerState, 'id' | 'alive'>[]): void {
    const dead = new Set(players.filter((player) => !player.alive).map((player) => player.id));
    for (const id of this.tracks.keys()) if (!dead.has(id)) this.tracks.delete(id);
  }

  /** SimpleBomb.bomb restarts nearby TombView physics after digging the map. */
  blast(point: Point, now: number, ground: DeathGround | null): void {
    for (const [id, track] of this.tracks) {
      if (now < track.startedAt) continue;
      this.sample(id, now, ground);
      if (!track.tomb.visible || Math.hypot(point.x - track.tomb.x, point.y - track.tomb.y) > 100) continue;
      if (!track.tomb.moving) {
        // Physics.stopMoving clears both Euler motions. Resume from this
        // collision pose and this impact tick, without simulating idle time.
        track.integrated = { x: track.tomb.x, y: track.tomb.y };
        track.vx = track.vy = 0;
        track.tombTick = Math.floor(Math.max(0, now - track.startedAt) / tickMs);
      }
      track.tomb.moving = true;
    }
  }

  sample(playerId: string, now: number, ground: DeathGround | null): DeathFrame | null {
    const track = this.tracks.get(playerId); if (!track) return null;
    const elapsed = Math.max(0, now - track.startedAt), target = Math.floor(elapsed / tickMs);
    while (track.tick < target) {
      track.tick++;
      this.stepSoul(track);
    }
    if (ground) while (track.tombTick < target && track.tomb.moving) { track.tombTick++; this.stepTomb(track, ground); }
    return { startedAt: track.startedAt, elapsed, soulFrame: track.soulFrame, soul: soulPose(track.pose), tomb: { ...track.tomb } };
  }

  /** Display-only lookahead on copies. Native Euler/collision and blink RNG
   * still advance exactly once per source tick in sample(), at any render rate.
   */
  sampleVisual(playerId: string, now: number, ground: DeathGround | null): VisualDeathFrame | null {
    const current = this.sample(playerId, now, ground), track = this.tracks.get(playerId);
    if (!current || !track) return null;
    const next: Track = { ...track, tomb: { ...track.tomb }, integrated: { ...track.integrated } };
    this.stepSoul(next);
    const mix = current.elapsed / tickMs - Math.floor(current.elapsed / tickMs);
    if (ground && next.tomb.moving) this.stepTomb(next, ground);
    // A removal/settle remains a source event. Only a still-visible position is
    // resampled, and never beyond the collision-tested next source position.
    const tomb = current.tomb.visible && next.tomb.visible ? { ...current.tomb,
      x: current.tomb.x + (next.tomb.x - current.tomb.x) * mix,
      y: current.tomb.y + (next.tomb.y - current.tomb.y) * mix,
      angle: current.tomb.angle + (next.tomb.angle - current.tomb.angle) * mix,
    } : current.tomb;
    return { ...current, tomb, nextSoulFrame: next.soulFrame, soulMix: current.soulFrame === next.soulFrame ? 0 : mix };
  }

  private stepSoul(track: Track): void {
    const blink = deathAssets.sourceRules.soulBlink;
    track.soulFrame = blink.idleFrame;
    if (track.eyeTicks < blink.initialEyeTicks) track.soulFrame = blink.eyeFrame;
    else {
      track.random ^= track.random << 13; track.random ^= track.random >>> 17; track.random ^= track.random << 5;
      if ((track.random >>> 0) / 4294967296 < blink.idleBlinkProbabilityPerUpdate) track.eyeTicks = 0;
    }
    track.eyeTicks++;
  }

  private stepTomb(track: Track, ground: DeathGround): void {
    const mass = 5, dt = tickMs / 1000, tomb = track.tomb;
    track.vx += (track.physics.wind - track.physics.drag * track.vx) / mass * dt;
    track.vy += (track.physics.gravity * 50 * mass - track.physics.drag * track.vy) / mass * dt;
    track.integrated.x += track.vx * dt; track.integrated.y += track.vy * dt;
    const target = track.integrated;
    // PhysicalObj.moveTo retains sub-three-pixel Euler steps until visible
    // displacement reaches three pixels, then probes old -> target by three.
    if (Math.hypot(target.x - tomb.x, target.y - tomb.y) < 3) return;
    const length = Math.max(Math.abs(Math.trunc(target.x) - Math.trunc(tomb.x)), Math.abs(Math.trunc(target.y) - Math.trunc(tomb.y)));
    for (let remaining = length; remaining > 0; remaining -= 3) {
      const amount = 1 - remaining / length;
      const point = { x: tomb.x + (target.x - tomb.x) * amount, y: tomb.y + (target.y - tomb.y) * amount };
      if (this.tombTouchesGround(point, ground)) {
        Object.assign(tomb, point, { moving: false, angle: terrainBodyAngle(ground, point) });
        track.integrated = { ...point }; track.vx = track.vy = 0; return;
      }
      if (Math.trunc(point.x) < 0 || Math.trunc(point.x) > ground.width || Math.trunc(point.y) > ground.height) {
        Object.assign(tomb, point, { visible: false, moving: false }); return;
      }
    }
    tomb.x = target.x; tomb.y = target.y;
  }

  private tombTouchesGround(point: Point, ground: DeathGround): boolean {
    const rect = deathAssets.sourceRules.tomb.testRect;
    const left = Math.max(0, point.x + rect.x), top = Math.max(0, point.y + rect.y);
    const right = Math.min(ground.width, point.x + rect.x + rect.width), bottom = Math.min(ground.height, point.y + rect.y + rect.height);
    // Ground.IsRectangeEmptyQuick checks the four clipped Rectangle corners,
    // including its right/bottom boundary rather than width−1/height−1.
    if (right <= left || bottom <= top) return ground.isSolid(0, 0);
    return [[left, top], [right, top], [left, bottom], [right, bottom]]
      .some(([x, y]) => ground.isSolid(Math.trunc(x), Math.trunc(y)));
  }

  reset(): void { this.tracks.clear(); }
  get size(): number { return this.tracks.size; }
  get anyMoving(): boolean { return [...this.tracks.values()].some(track => track.tomb.visible && track.tomb.moving); }
}
